import { memo, useEffect, useRef, useState } from "react";
import { Badge, Icon } from "@elaniintc/goes-design-system";
import { getRemainingTime } from "@/utils/procedures-ui";

const TimerComponent = ({
  deadline,
  onExpire,
  disabled = false,
  externalStartDate,
}: {
  deadline: Date | null | undefined;
  onExpire: () => void;
  disabled?: boolean;
  externalStartDate?: Date | null | undefined;
}) => {
  const [remainingTime, setRemainingTime] = useState<string>("");
  const hasExpiredRef = useRef(false);

  // Check if process has not started yet based on external start date
  const hasNotStarted = externalStartDate && new Date() < externalStartDate;

  // Update the countdown timer every second
  useEffect(() => {
    if (!deadline || disabled) {
      setRemainingTime("");
      return;
    }

    // If process hasn't started yet, show initial time but don't countdown
    if (hasNotStarted) {
      const initialTime = getRemainingTime(deadline);
      setRemainingTime(initialTime);
      return; // Exit early - no interval needed for frozen state
    }

    // Check if deadline is already in the past
    const now = new Date();
    if (deadline <= now) {
      setRemainingTime("");
      if (!hasExpiredRef.current) {
        hasExpiredRef.current = true;
        onExpire();
      }
      return;
    }

    // Reset the expiration flag when we get a new valid deadline
    hasExpiredRef.current = false;

    // Function to update the timer
    const updateTimer = () => {
      const currentTime = getRemainingTime(deadline);
      setRemainingTime(currentTime);

      // If time has run out and we haven't fired expiration yet
      if (!currentTime && !hasExpiredRef.current) {
        hasExpiredRef.current = true;
        onExpire();
      }

      return currentTime;
    };

    // Initial update
    updateTimer();

    // Set up interval to update every second
    const intervalId = setInterval(() => {
      const currentTime = updateTimer();

      // If time has run out, clear the interval
      if (!currentTime) {
        clearInterval(intervalId);
      }
    }, 1000);

    // Clean up interval on component unmount or when deadline changes
    return () => clearInterval(intervalId);
  }, [deadline, onExpire, disabled, hasNotStarted]);

  if (!deadline || !remainingTime || disabled) {
    return null;
  }

  // If process hasn't started yet, show pending state
  if (hasNotStarted) {
    return (
      <span className="cursor-default shrink-0 gap-1 px-2 [&_svg]:shrink-0 py-0.5 border-[0.5px] font-normal transition-all bg-neutral-object-accent-silent border-neutral text-body h-6 text-sm leading-2xl rounded-md hover:bg-neutral-object-accent-quiet hover:text-body-loud flex items-center w-fit opacity-60">
        {remainingTime}
        <i
          className="fas fa-pause fa-xs text-neutral-quiet"
          aria-hidden="true"
        ></i>
      </span>
    );
  }

  // Normal active state
  return (
    <Badge
      size="sm"
      shape="square"
      variant="warning"
      fill="ghost"
      className="flex items-center mr-2 w-fit cursor-default"
    >
      {remainingTime}
      <Icon type="fas" name="clock" size="xs" className="text-warning-quiet" />
    </Badge>
  );
};

export const DeadlineTimer = memo(TimerComponent);
DeadlineTimer.displayName = "DeadlineTimer";
